/**
 * Service de gestion des notifications
 * Permet de récupérer, marquer comme lues et supprimer les notifications
 */

import { supabase } from '../supabaseClient';

// Interface pour une notification
export interface Notification {
  id: string;
  user_id: string;
  message: string;
  type: string;
  related_id?: string | null;
  is_read: boolean;
  is_deleted: boolean;
  is_email_required: boolean;
  email_sent_at?: string | null;
  created_at: string;
}

/**
 * Récupère toutes les notifications non lues de l'utilisateur connecté
 */
export const getUnreadNotifications = async (): Promise<{ data: Notification[] | null; error: any }> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      return { data: null, error: 'Utilisateur non connecté' };
    }

    const { data, error } = await supabase
      .from('notifications')
      .select('*')
      .eq('user_id', user.id)
      .eq('is_read', false)
      .eq('is_deleted', false)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Erreur lors de la récupération des notifications:', error);
      return { data: null, error };
    }

    return { data, error: null };
  } catch (error) {
    console.error('Exception lors de la récupération des notifications:', error);
    return { data: null, error };
  }
};

/**
 * Récupère toutes les notifications de l'utilisateur connecté (lues et non lues)
 */
export const getAllNotifications = async (limit = 50): Promise<{ data: Notification[] | null; error: any }> => {
  try {
    const { data, error } = await supabase
      .from('notifications')
      .select('*')
      .eq('is_deleted', false)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) {
      console.error('Erreur lors de la récupération des notifications:', error);
      return { data: null, error };
    }

    return { data, error: null };
  } catch (error) {
    console.error('Exception lors de la récupération des notifications:', error);
    return { data: null, error };
  }
};

/**
 * Marque une notification comme lue
 */
export const markNotificationAsRead = async (notificationId: string): Promise<{ success: boolean; error: any }> => {
  try {
    const { error } = await supabase
      .rpc('mark_notification_as_read', {
        p_notification_id: notificationId
      });

    if (error) {
      console.error('Erreur lors de la mise à jour de la notification:', error);
      return { success: false, error };
    }

    return { success: true, error: null };
  } catch (error) {
    console.error('Exception lors de la mise à jour de la notification:', error);
    return { success: false, error };
  }
};

/**
 * Marque toutes les notifications comme lues
 */
export const markAllNotificationsAsRead = async (): Promise<{ success: boolean; error: any }> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      return { success: false, error: 'Utilisateur non connecté' };
    }

    const { error } = await supabase
      .from('notifications')
      .update({ is_read: true })
      .eq('user_id', user.id)
      .eq('is_read', false)
      .eq('is_deleted', false);

    if (error) {
      console.error('Erreur lors de la mise à jour des notifications:', error);
      return { success: false, error };
    }

    return { success: true, error: null };
  } catch (error) {
    console.error('Exception lors de la mise à jour des notifications:', error);
    return { success: false, error };
  }
};

/**
 * Supprime une notification (soft delete)
 */
export const deleteNotification = async (notificationId: string): Promise<{ success: boolean; error: any }> => {
  try {
    const { error } = await supabase
      .rpc('delete_notification', {
        p_notification_id: notificationId
      });

    if (error) {
      console.error('Erreur lors de la suppression de la notification:', error);
      return { success: false, error };
    }

    return { success: true, error: null };
  } catch (error) {
    console.error('Exception lors de la suppression de la notification:', error);
    return { success: false, error };
  }
};

/**
 * Récupère le nombre de notifications non lues
 */
export const getUnreadNotificationsCount = async (): Promise<{ count: number; error: any }> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      return { count: 0, error: null };
    }

    const { count, error } = await supabase
      .from('notifications')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', user.id)
      .eq('is_read', false)
      .eq('is_deleted', false);

    if (error) {
      console.error('Erreur lors du comptage des notifications:', error);
      return { count: 0, error };
    }

    return { count: count || 0, error: null };
  } catch (error) {
    console.error('Exception lors du comptage des notifications:', error);
    return { count: 0, error: null };
  }
};

